import { useNavigate } from "react-router-dom";
import { differenceInCalendarDays } from "date-fns";
import { ArrowRight, Paperclip, Printer, Receipt } from "lucide-react";
import { Sheet, SheetContent, SheetDescription, SheetTitle } from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { StatusChip } from "@/components/ui/status-chip";
import { formatDate } from "@/lib/formatDate";
import type { AdmissionRow } from "@/lib/ipdApi";
import { admissionStatus, bedLabel } from "@/components/IPD/admissionStatus";

const rupees = (n: number | null | undefined) => `₹${Number(n ?? 0).toLocaleString("en-IN")}`;

/** Row → side panel on the IPD list: where the patient is, for how long, and what they owe. */
export default function AdmissionPeekPanel({ admission, onClose }: { admission: AdmissionRow | null; onClose: () => void }) {
  const navigate = useNavigate();
  const a = admission;
  const open = (tab?: string) => a && navigate(`/ipd/admissions/${a.id}${tab ? `?tab=${tab}` : ""}`);

  const stayDays = a?.admission_date
    ? differenceInCalendarDays(a.discharge_date ? new Date(a.discharge_date) : new Date(), new Date(a.admission_date))
    : null;
  const due = Number(a?.balance_due ?? 0);

  return (
    <Sheet open={!!a} onOpenChange={(o) => { if (!o) onClose(); }}>
      <SheetContent side="right" className="w-full sm:max-w-[420px] p-0 gap-0 flex flex-col">
        {a && (
          <>
            <div className="px-4 py-3 pr-12 border-b border-border">
              <SheetTitle className="text-base font-semibold flex items-center gap-2 min-w-0">
                <span className="truncate capitalize">{a.patient_name}</span>
                {/* Same chip as the list row */}
                {a.status === 'admitted' && a.discharge_advised_at
                  ? <StatusChip tone="warning">Discharge advised</StatusChip>
                  : <StatusChip tone={admissionStatus(a.status).tone}>{admissionStatus(a.status).label}</StatusChip>}
              </SheetTitle>
              <SheetDescription className="text-xs text-muted-foreground">
                {a.patient_mrn && <>MRN <span className="font-mono text-foreground/80">{a.patient_mrn}</span> · </>}
                Adm. <span className="font-mono text-foreground/80">{a.admission_number}</span>
              </SheetDescription>
            </div>

            <div className="flex-1 overflow-y-auto divide-y divide-border">
              <section className="px-4 py-3">
                <dl className="grid grid-cols-[7rem_minmax(0,1fr)] gap-y-1.5 text-sm">
                  <dt className="text-muted-foreground">Ward / bed</dt>
                  <dd className="text-foreground">{a.ward_name} · {bedLabel(a.bed_number)}</dd>
                  <dt className="text-muted-foreground">Doctor</dt>
                  <dd className="text-foreground">{a.admitting_doctor_name || "—"}</dd>
                  <dt className="text-muted-foreground">Admitted</dt>
                  <dd className="text-foreground tabular-nums">{a.admission_date ? formatDate(a.admission_date) : "—"}</dd>
                  {a.discharge_date && (
                    <>
                      <dt className="text-muted-foreground">Discharged</dt>
                      <dd className="text-foreground tabular-nums">{formatDate(a.discharge_date)}</dd>
                    </>
                  )}
                  {stayDays != null && (
                    <>
                      <dt className="text-muted-foreground">Length of stay</dt>
                      <dd className="text-foreground tabular-nums">{stayDays === 0 ? (a.discharge_date ? "Same day" : "Admitted today") : `${stayDays} day${stayDays === 1 ? "" : "s"}`}</dd>
                    </>
                  )}
                </dl>
              </section>

              <section className="px-4 py-3">
                <h3 className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-1.5">
                  Bill {a.invoice_number && <span className="font-mono normal-case tracking-normal font-medium">{a.invoice_number}</span>}
                </h3>
                {a.total_amount == null ? (
                  <p className="text-sm text-muted-foreground">No bill yet.</p>
                ) : (
                  <div className="space-y-1.5 text-sm">
                    <div className="flex justify-between"><span className="text-muted-foreground">Total</span><span className="font-mono tabular-nums text-foreground">{rupees(a.total_amount)}</span></div>
                    <div className="flex justify-between"><span className="text-muted-foreground">Paid</span><span className="font-mono tabular-nums text-foreground">{rupees(a.paid_amount)}</span></div>
                    {Number(a.available_advance) > 0 && (
                      <div className="flex justify-between"><span className="text-muted-foreground">Advance available</span><span className="font-mono tabular-nums text-foreground">{rupees(a.available_advance)}</span></div>
                    )}
                    <div className="flex justify-between items-center border-t border-border pt-2">
                      <span className="font-medium text-foreground">Balance due</span>
                      {due > 0
                        ? <span className="rounded bg-highlight px-2 py-0.5 font-mono font-semibold tabular-nums text-highlight-foreground">{rupees(due)}</span>
                        : <span className="font-mono tabular-nums text-muted-foreground">{rupees(0)}</span>}
                    </div>
                  </div>
                )}
              </section>
            </div>

            <div className="flex flex-wrap items-center gap-2 px-4 py-3 border-t border-border bg-card">
              <Button variant="outline" size="sm" className="gap-1.5" onClick={() => open("charges")}><Receipt className="w-4 h-4" /> Bill</Button>
              <Button variant="outline" size="sm" className="gap-1.5" onClick={() => open("discharge")}><Printer className="w-4 h-4" /> Discharge</Button>
              <Button variant="ghost" size="sm" className="h-8 w-8 p-0" onClick={() => open("attachments")} aria-label="Attachments" title="Attachments"><Paperclip className="w-4 h-4" /></Button>
              <Button size="sm" className="ml-auto gap-1.5" onClick={() => open()}>Open <ArrowRight className="w-4 h-4" /></Button>
            </div>
          </>
        )}
      </SheetContent>
    </Sheet>
  );
}
